// settings.ts — bot settings shape (spec D13). Hand-written here (not `z.infer`-derived)
// because shared-types has no zod dependency; `packages/validation/src/bot-settings.ts` owns
// the zod schema and its own test asserts the two stay in sync via a `satisfies` check.

export type BotSettingsPosition = 'bottom-right' | 'bottom-left';
export type LeadCaptureMode = 'off' | 'on-intent' | 'always' | 'pre-chat';

/** Widget-i18n spec — languages the widget's own chrome can render. Lives here (not
 * `apps/widget`) because the dashboard's language select (Task 3) needs the same union; the
 * actual `WidgetStrings` table stays in `apps/widget/src/i18n.ts` so it never ships to the
 * dashboard bundle. ADDING a language is a data change: one entry here, one table in i18n.ts.
 *
 * REMOVING or renaming one is not the symmetric operation it looks like: any bot that had
 * pinned that locale (`BotSettings.language`) now holds a stored value outside this union.
 * `packages/validation/src/bot-settings.ts`'s `botLanguageSettingSchema` degrades that value to
 * `'auto'` via `.catch('auto')` rather than failing to parse — without it, that one stored
 * value would 500 every public route for that bot, including chat, not just its config
 * (`getPublicBot`, apps/api/src/app.ts, runs this schema on all of them). Shrinking this array
 * safely still means checking for bots pinned to the locale being removed first; the `.catch`
 * is the failure-mode guard, not a reason skipping that check is fine. */
export const WIDGET_LOCALES = [
  'en',
  'vi',
  'es',
  'pt',
  'fr',
  'de',
  'it',
  'nl',
  'pl',
  'tr',
  'id',
  'ja',
  'ko',
  'zh',
  'hi',
] as const;
export type WidgetLocale = (typeof WIDGET_LOCALES)[number];

/** Human-readable label per `WidgetLocale`, for the dashboard's language select (Task 3) — the
 * one thing about a locale that can't be derived from `WIDGET_LOCALES` itself. Lives right here,
 * next to `WIDGET_LOCALES`, so adding a locale to that array makes the next line impossible to
 * miss. A `Record`, not `Partial<Record<...>>`, so a locale added there without a label here is
 * a type error at build time, not a blank option in the select at runtime. */
export const WIDGET_LOCALE_LABELS: Record<WidgetLocale, string> = {
  en: 'English',
  vi: 'Tiếng Việt',
  es: 'Español',
  pt: 'Português',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
  tr: 'Türkçe',
  id: 'Bahasa Indonesia',
  ja: '日本語',
  ko: '한국어',
  zh: '中文（简体）',
  hi: 'हिन्दी',
};

/** `BotSettings`' per-bot chrome-language preference. `'auto'` (the default) resolves from the
 * visitor's browser at render time; a fixed `WidgetLocale` pins the chrome regardless of the
 * visitor's browser. See `apps/widget/src/i18n.ts`'s `resolveLocale`. */
export type BotLanguageSetting = 'auto' | WidgetLocale;

/** Chat-status-polish spec (2026-08-22), §B — five pronoun-free "still working" sentences per
 * locale, shown one at a time (picked with `Math.random()` at the call site, not seeded — a
 * repeat within a session is fine) alongside the pulsing accent dot while a `status:searching`
 * frame is in effect. Every sentence ends with "…" and carries no first-person pronoun in its
 * own language ("Searching the site…", never "I'm searching…") — see the design note in the
 * spec for why: the widget/playground surfaces never speak as "I", only report what's
 * happening. This is the SINGLE source of truth for all three surfaces (widget, in-app
 * playground, public playground): unlike `WidgetStrings` (kept out of `@repo/shared-types` on
 * purpose so the dashboard bundle never carries widget chrome strings it doesn't render), this
 * table genuinely IS needed by every consumer, and the widget already imports `WIDGET_LOCALES`
 * as a value from this same file with no bundle-budget issue (see `apps/widget/src/i18n.ts`) —
 * so there is no reason to fork a second copy for the widget to avoid an import that was never
 * expensive. `apps/widget/src/i18n.ts`'s `STRINGS[locale].searchingStatus` is a direct reference
 * into this table, not a duplicate. */
export const SEARCHING_STATUS: Record<WidgetLocale, readonly string[]> = {
  en: [
    'Searching the site…',
    'Looking that up…',
    'Checking the pages…',
    'Reading through the docs…',
    'One moment, finding that…',
  ],
  vi: [
    'Đang tìm trên trang…',
    'Để tra lại…',
    'Đang lật qua các trang…',
    'Đang đọc tài liệu…',
    'Chờ chút, đang kiểm tra…',
  ],
  es: [
    'Buscando en el sitio…',
    'Comprobando eso…',
    'Revisando las páginas…',
    'Leyendo la documentación…',
    'Un momento, verificando eso…',
  ],
  pt: [
    'Pesquisando no site…',
    'Verificando isso…',
    'Consultando as páginas…',
    'Lendo a documentação…',
    'Um momento, conferindo isso…',
  ],
  fr: [
    'Recherche sur le site…',
    'Vérification en cours…',
    'Consultation des pages…',
    'Lecture de la documentation…',
    'Un instant, recherche en cours…',
  ],
  de: [
    'Suche auf der Website…',
    'Das wird geprüft…',
    'Seiten werden durchsucht…',
    'Dokumentation wird gelesen…',
    'Einen Moment, das wird geklärt…',
  ],
  it: [
    'Ricerca nel sito…',
    'Verifica in corso…',
    'Controllo delle pagine…',
    'Lettura della documentazione…',
    'Un attimo, controllo in corso…',
  ],
  nl: [
    'Zoeken op de site…',
    'Even nakijken…',
    "Pagina's doorzoeken…",
    'Documentatie doorlezen…',
    'Momentje, dit wordt gecontroleerd…',
  ],
  pl: [
    'Przeszukiwanie strony…',
    'Sprawdzanie tego…',
    'Przeglądanie stron…',
    'Czytanie dokumentacji…',
    'Chwila, trwa sprawdzanie…',
  ],
  tr: [
    'Sitede aranıyor…',
    'Kontrol ediliyor…',
    'Sayfalar taranıyor…',
    'Belgeler okunuyor…',
    'Bir dakika, inceleniyor…',
  ],
  id: [
    'Mencari di situs…',
    'Memeriksa itu…',
    'Menelusuri halaman…',
    'Membaca dokumentasi…',
    'Sebentar, sedang diperiksa…',
  ],
  ja: [
    'サイト内を検索中…',
    '確認しています…',
    'ページを確認中…',
    'ドキュメントを読んでいます…',
    '少々お待ちください、確認中…',
  ],
  ko: [
    '사이트 검색 중…',
    '확인하는 중…',
    '페이지 확인 중…',
    '문서를 읽는 중…',
    '잠시만요, 확인 중…',
  ],
  zh: [
    '正在搜索网站…',
    '正在核实…',
    '正在查看页面…',
    '正在阅读文档…',
    '请稍等，正在核实…',
  ],
  hi: [
    'साइट पर खोजा जा रहा है…',
    'जांच की जा रही है…',
    'पेज देखे जा रहे हैं…',
    'दस्तावेज़ पढ़े जा रहे हैं…',
    'एक क्षण, पता किया जा रहा है…',
  ],
};

/** Resolves a `BotLanguageSetting` to the `WidgetLocale` a PLAYGROUND surface (in-app or
 * public — never the widget itself) renders `SEARCHING_STATUS` sentences in. Deliberately
 * simpler than the widget's own `resolveLocale` (`apps/widget/src/i18n.ts`): a playground has
 * no "visitor's browser" to match against distinct from the tester's own, so `'auto'` just
 * resolves to `'en'` rather than sniffing `navigator.languages`. */
export function resolvePlaygroundLocale(setting: BotLanguageSetting): WidgetLocale {
  return setting === 'auto' ? 'en' : setting;
}
/** Transcript-leadlevel spec §B — how aggressively the `on-intent` prompt asks for contact
 * details. Only meaningful when `leadCapture === 'on-intent'`; ignored by every other mode. */
export type LeadProactivenessLevel = 'subtle' | 'balanced' | 'proactive';

export interface BotAppearanceSettings {
  /** 6-digit hex color, e.g. "#4f46e5" */
  color: string;
  botName: string;
  welcome: string;
  /** ≤4 entries, each ≤80 chars */
  quickQuestions: string[];
  position: BotSettingsPosition;
}

export interface BotSettings {
  appearance: BotAppearanceSettings;
  leadCapture: LeadCaptureMode;
  /** Default `'balanced'` (spec §B). Stored alongside `leadCapture` in the same settings
   * JSON blob — no separate migration needed. */
  leadLevel: LeadProactivenessLevel;
  /** bare hostnames only (no scheme, no path) — empty array means "allow all" (see D11) */
  allowedDomains: string[];
  /** Badge opt-in spec (2026-08-11) — the owner's choice, default `false`. Only decides
   * anything when `PLAN_LIMITS[plan].badgeRequired` is `false` (every paid plan, internal): a
   * Free workspace's badge is required regardless of this value. See `resolveShowsBadge`
   * (`@repo/shared-types/limits.ts`) for the one place the two combine. */
  showBadge: boolean;
  /** Widget-i18n spec, Task 3 — the owner's pinned widget-chrome language. Default `'auto'`
   * (see `BotLanguageSetting` above). Same shape as `leadCapture`/`leadLevel`: a field in the
   * existing settings JSON blob, no migration — an existing bot's stored settings simply lack
   * this key, and `botSettingsSchema.parse` (packages/validation) fills the default in. */
  language: BotLanguageSetting;
}
